import React, { useState } from 'react';
import { UserIcon, ShieldIcon, CreditCardIcon, BellIcon, BellIcon as NotificationIcon, BookIcon, SettingsIcon, SlidersHorizontalIcon, CheckCircleIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { ContentFilterSection } from '../../components/account/ContentFilterSection';
import { useReadingPreferences } from '../../context/ReadingPreferencesContext';
import { SettingsLayout, SettingsSection, ToggleSwitch, NotificationToggleGroup, PasswordFields, AccountActionsSection, SocialLinksEditor } from '../../components/account';
const ReaderAccountSettings = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    preferences: savedPreferences,
    savePreferences
  } = useReadingPreferences();
  const [preferencesSaved, setPreferencesSaved] = useState(false);
  const [activeSection, setActiveSection] = useState('profile');
  const [formData, setFormData] = useState({
    email: 'reader@example.com',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    profile: {
      username: 'bookworm42',
      displayName: 'Sarah Johnson',
      pronouns: 'she/her',
      location: 'Portland, OR',
      bio: "Avid reader with a passion for fantasy and romance. Always looking for stories with strong character development and immersive worlds. When I'm not reading, I'm probably thinking about reading.",
      socialLinks: [{
        platform: 'Instagram',
        url: 'https://instagram.com/bookworm42'
      }, {
        platform: 'Twitter',
        url: 'https://twitter.com/bookworm42'
      }, {
        platform: 'Goodreads',
        url: 'https://goodreads.com/bookworm42'
      }],
      currentRead: 'The Invisible Life of Addie LaRue by V.E. Schwab'
    },
    // Seeded from the shared, persisted store so the form shows what was
    // actually saved rather than a hardcoded default.
    readingPreferences: savedPreferences,
    emailNotifications: {
      newReleases: true,
      authorUpdates: true,
      orderConfirmations: true,
      promotions: false
    },
    pushNotifications: {
      newReleases: false,
      authorUpdates: true,
      orderConfirmations: true,
      promotions: false
    },
    twoFactorEnabled: false
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    if (type === 'checkbox') {
      if (name.includes('.')) {
        const [parent, child] = name.split('.');
        setFormData({
          ...formData,
          [parent]: {
            ...(formData[parent as keyof typeof formData] as Record<string, boolean>),
            [child]: checked
          }
        });
      } else {
        setFormData({
          ...formData,
          [name]: checked
        });
      }
    } else if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...(formData[parent as keyof typeof formData] as Record<string, string>),
          [child]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  const handleSocialLinkChange = (index: number, field: 'platform' | 'url', value: string) => {
    const updatedLinks = [...formData.profile.socialLinks];
    updatedLinks[index][field] = value;
    setFormData({
      ...formData,
      profile: {
        ...formData.profile,
        socialLinks: updatedLinks
      }
    });
  };
  const addSocialLink = () => {
    setFormData({
      ...formData,
      profile: {
        ...formData.profile,
        socialLinks: [...formData.profile.socialLinks, {
          platform: '',
          url: ''
        }]
      }
    });
  };
  const removeSocialLink = (index: number) => {
    const updatedLinks = [...formData.profile.socialLinks];
    updatedLinks.splice(index, 1);
    setFormData({
      ...formData,
      profile: {
        ...formData.profile,
        socialLinks: updatedLinks
      }
    });
  };
  const handleMultiSelectChange = (section: 'genres' | 'tropes', value: string[]) => {
    setFormData({
      ...formData,
      readingPreferences: {
        ...formData.readingPreferences,
        [section]: value
      }
    });
  };
  const handleContentFilterChange = (field: 'allowlistEnabled' | 'allowlistTags' | 'blocklistTags', value: boolean | string[]) => {
    setFormData({
      ...formData,
      readingPreferences: {
        ...formData.readingPreferences,
        [field]: value
      }
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Saving here is the ONLY thing that changes stored preferences — filter
    // tweaks made while browsing never write back to this.
    savePreferences(formData.readingPreferences);
    setPreferencesSaved(true);
    window.setTimeout(() => setPreferencesSaved(false), 3000);
  };
  const navigationSections = [{
    id: 'profile',
    label: 'Profile Information',
    icon: <UserIcon className="h-5 w-5" data-id="element-4291" data-mp="e0daay2px4pm" />
  }, {
    id: 'reading',
    label: 'Reading Preferences',
    icon: <BookIcon className="h-5 w-5" data-id="element-4292" data-mp="8ynw59k78axb" />
  }, {
    id: 'security',
    label: 'Security',
    icon: <ShieldIcon className="h-5 w-5" data-id="element-4293" data-mp="4yvuzhx4x71z" />
  }, {
    id: 'payment',
    label: 'Payment Methods',
    icon: <CreditCardIcon className="h-5 w-5" data-id="element-4294" data-mp="1lyjibbvv4n" />
  }, {
    id: 'notifications',
    label: 'Notifications',
    icon: <NotificationIcon className="h-5 w-5" data-id="element-4295" data-mp="1udk4nbeccawb" />
  }, {
    id: 'account',
    label: 'Account Actions',
    icon: <SettingsIcon className="h-5 w-5" data-id="element-4296" data-mp="2dhnbdwmxbfex" />
  }];
  const genreOptions = ['Fantasy', 'Romance', 'Mystery', 'Historical Fiction', 'Science Fiction', 'Thriller', 'Horror', 'Contemporary', 'Literary Fiction', 'Young Adult', 'Non-fiction', 'Memoir', 'Biography', 'Self-help', 'Poetry'];
  const tropeOptions = ['Enemies to Lovers', 'Friends to Lovers', 'Found Family', 'Chosen One', 'Slow Burn', 'Fake Dating', 'Second Chance', 'Forbidden Love', 'Morally Grey Characters', 'Marriage of Convenience', 'Forced Proximity', 'Redemption Arc', 'Secret Identity', 'Fish Out of Water', 'Unreliable Narrator'];
  const inputClass = `mt-1 block w-full rounded-md border sm:text-sm focus:outline-none focus:ring-2 px-3 py-2 ${rt.inputFocus} ${dk.input}`;
  const labelClass = `block text-sm font-medium ${dk.textSecondary}`;
  const saveSections = ['profile', 'reading', 'security', 'payment', 'notifications'];
  return <SettingsLayout title="Account Settings" subtitle="Manage your profile, reading preferences, and account details" headerIcon={<UserIcon className={`h-6 w-6 ${rt.textPrimary}`} data-id="element-4298" data-mp="1i9bmb13cfxy0" />} navigationSections={navigationSections} activeSection={activeSection} setActiveSection={setActiveSection} onSubmit={handleSubmit} showSaveButton={saveSections.includes(activeSection)} data-id="element-4297" data-mp="19ahrng9kphn3">
      {/* Profile Information */}
      {activeSection === 'profile' && <SettingsSection icon={<UserIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-4300" data-mp="zaqypj0nvap4" />} title="Profile Information" subtitle="Your public profile details" data-id="element-4299" data-mp="lgpzko5srlo8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4301" data-mp="uheah9zw95ww">
            <div data-id="element-4302" data-mp="6slnw455waey">
              <label htmlFor="username" className={labelClass} data-id="element-4303" data-mp="6s3ioi0iuluw">
                Username
              </label>
              <input type="text" id="username" name="profile.username" value={formData.profile.username} onChange={handleInputChange} className={inputClass} data-id="element-4304" data-mp="1sqxs2t7nouy" />
            </div>
            <div data-id="element-4305" data-mp="3mdhlv0w0fyh7">
              <label htmlFor="displayName" className={labelClass} data-id="element-4306" data-mp="3mh5apmqifwpl">
                Display Name
              </label>
              <input type="text" id="displayName" name="profile.displayName" value={formData.profile.displayName} onChange={handleInputChange} className={inputClass} data-id="element-4307" data-mp="7ug42m9i0pw" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4308" data-mp="dyzel1dw8myn">
            <div data-id="element-4309" data-mp="e1d7p4p0jp3k">
              <label htmlFor="pronouns" className={labelClass} data-id="element-4310" data-mp="iyc5pybssg71">
                Pronouns
              </label>
              <input type="text" id="pronouns" name="profile.pronouns" value={formData.profile.pronouns} onChange={handleInputChange} className={inputClass} data-id="element-4311" data-mp="nx6t7dkpmsxh" />
            </div>
            <div data-id="element-4312" data-mp="1yhbfx436451p">
              <label htmlFor="location" className={labelClass} data-id="element-4313" data-mp="1ygrv9fm4v2jz">
                Location
              </label>
              <input type="text" id="location" name="profile.location" value={formData.profile.location} onChange={handleInputChange} className={inputClass} data-id="element-4314" data-mp="1tj00mdu3t2c3" />
            </div>
          </div>
          <div data-id="element-4315" data-mp="2hf44t2gyieg3">
            <label htmlFor="bio" className={labelClass} data-id="element-4316" data-mp="2heumm125lbky">
              Bio
            </label>
            <textarea id="bio" name="profile.bio" rows={4} value={formData.profile.bio} onChange={handleInputChange} className={inputClass} data-id="element-4317" data-mp="2cgb3s9iuwfwt" />
          </div>
          <div data-id="element-4318" data-mp="15unn9ghikhhv">
            <label htmlFor="currentRead" className={labelClass} data-id="element-4319" data-mp="15u4827j9077j">
              Currently Reading
            </label>
            <input type="text" id="currentRead" name="profile.currentRead" value={formData.profile.currentRead} onChange={handleInputChange} className={inputClass} data-id="element-4320" data-mp="11u3sgjkexpyw" />
          </div>

          {/* Profile Picture */}
          <div className={`p-5 rounded-xl border ${dk.border} ${dk.surfaceMuted}`} data-id="element-4321" data-mp="1os70kwb08ma3">
            <h4 className={`text-sm font-semibold mb-4 ${dk.textHeading}`} data-id="element-4322" data-mp="1kreq5zqecbc8">
              Profile Picture
            </h4>
            <div className="flex items-center gap-5" data-id="element-4323" data-mp="1yl5r4jq2amwc">
              <div className={`h-20 w-20 rounded-full flex items-center justify-center flex-shrink-0 ${rt.bgMedium}`} data-id="element-4324" data-mp="1tl9n4khd6vgs">
                <UserIcon className={`h-10 w-10 ${rt.textPrimary}`} data-id="element-4325" data-mp="28f4t9iq4y6oa" />
              </div>
              <div data-id="element-4326" data-mp="23gbe7lv7a7m0">
                <p className={`text-sm mb-2 ${dk.textMuted}`} data-id="element-4327" data-mp="2n4h8d1hcfb0j">
                  Upload a profile picture to personalize your account
                </p>
                <button type="button" className={`px-4 py-2 text-sm font-medium rounded-lg ${rt.secondaryButton}`} data-id="element-4328" data-mp="2r59jk09rzpzg">
                  Upload Image
                </button>
              </div>
            </div>
          </div>

          <SocialLinksEditor links={formData.profile.socialLinks} platformOptions={['Instagram', 'Twitter', 'Goodreads', 'TikTok', 'Other']} onLinkChange={handleSocialLinkChange} onAdd={addSocialLink} onRemove={removeSocialLink} data-id="element-4329" data-mp="23f5r8p21fcnr" />

          {/* Email */}
          <div className={`pt-6 border-t ${dk.border}`} data-id="element-4330" data-mp="1kinpdeth5wds">
            <h4 className={`text-sm font-semibold mb-4 ${dk.textHeading}`} data-id="element-4331" data-mp="1pg61fe2sgrq0">
              Email Address
            </h4>
            <div data-id="element-4332" data-mp="16c2izvkufyrk">
              <label htmlFor="email" className={labelClass} data-id="element-4333" data-mp="16cm3nk1vp19a">
                Email
              </label>
              <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} className={inputClass} data-id="element-4334" data-mp="11jj8x8ld7lri" />
            </div>
          </div>
        </SettingsSection>}

      {/* Reading Preferences */}
      {activeSection === 'reading' && <SettingsSection icon={<BookIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-4336" data-mp="1k9is5euiy6n0" />} title="Reading Preferences" subtitle="Customize your reading experience" data-id="element-4335" data-mp="1kcn26suo7wll">
          {/* Makes the connection to discovery explicit — otherwise there's no
              way to tell these settings reach the Browse filters at all. */}
          <div className={`flex items-start gap-2 p-3 mb-5 rounded-lg text-xs ${rt.bgLight} ${dk.textSecondary}`} data-id="element-4337" data-mp="3v3mzxfbay1z3">
            <SlidersHorizontalIcon className={`h-3.5 w-3.5 flex-shrink-0 mt-0.5 ${rt.textPrimary}`} data-id="element-4338" data-mp="2amnjdj7indmx" />
            <span data-id="element-4339" data-mp="2fkyststwfm4m">
              These preferences preselect your Browse filters. You can always
              adjust filters while browsing without changing what&apos;s saved
              here.
            </span>
          </div>
          {preferencesSaved && <div className={`flex items-center gap-2 p-3 mb-5 rounded-lg text-xs ${dk.isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-800'}`} role="status" data-id="element-4340" data-mp="2u8jwbnuornd2">
              <CheckCircleIcon className="h-3.5 w-3.5 flex-shrink-0" data-id="element-4341" data-mp="33at62bi4rcpe" />
              Reading preferences saved. Browse filters will use them.
            </div>}

          <ContentFilterSection allowlistEnabled={formData.readingPreferences.allowlistEnabled} onAllowlistToggle={enabled => handleContentFilterChange('allowlistEnabled', enabled)} allowlistTags={formData.readingPreferences.allowlistTags} onAllowlistChange={tags => handleContentFilterChange('allowlistTags', tags)} blocklistTags={formData.readingPreferences.blocklistTags} onBlocklistChange={tags => handleContentFilterChange('blocklistTags', tags)} data-id="element-4342" data-mp="18x20po6u3ezr" />

          {/* Discovery Save Destination */}
          <div className={`pt-6 border-t ${dk.border}`} data-id="element-4343" data-mp="2gmuxhbswiwro">
            <h4 className={`text-sm font-semibold mb-1 ${dk.textHeading}`} data-id="element-4344" data-mp="2gofqgwzki7nf">
              Book Discovery
            </h4>
            <p className={`text-xs mb-4 ${dk.textFaint}`} data-id="element-4345" data-mp="2ugdu4saifbc7">
              Choose where your liked books are saved when you swipe right.
            </p>
            <div className="space-y-2" data-id="element-4346" data-mp="21x6wycsjo250">
              {[{
            value: 'wishlist',
            label: 'Wishlist',
            description: 'Your public or private wishlist'
          }, {
            value: 'want-to-read',
            label: 'Want to Read',
            description: 'Your bookshelf "Want to Read" shelf'
          }, {
            value: 'tracker',
            label: 'My Tracker',
            description: 'Your new release tracker'
          }, {
            value: 'ask',
            label: 'Ask me each time',
            description: 'A quick prompt appears after each save'
          }].map(({
            value,
            label,
            description
          }) => <label key={value} className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${formData.readingPreferences.discoverySaveDestination === value ? `${rt.bgLight} border-current ${rt.textPrimary}` : `${dk.isDark ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-200 hover:bg-gray-50'}`}`} data-id="element-4347" data-mp="1wmuna0giap18">
                  <input type="radio" name="readingPreferences.discoverySaveDestination" value={value} checked={formData.readingPreferences.discoverySaveDestination === value} onChange={handleInputChange} className={`mt-0.5 ${rt.checkbox}`} data-id="element-4348" data-mp="2sqjv5xmv6789" />
                  <div data-id="element-4349" data-mp="25194xa353v8d">
                    <p className={`text-sm font-medium ${dk.textHeading}`} data-id="element-4350" data-mp="250po5wp87cvx">
                      {label}
                    </p>
                    <p className={`text-xs ${dk.textMuted}`} data-id="element-4351" data-mp="204s3xfaudmdv">{description}</p>
                  </div>
                </label>)}
            </div>
          </div>
        </SettingsSection>}

      {/* Security */}
      {activeSection === 'security' && <SettingsSection icon={<ShieldIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-4353" data-mp="3pfj4lfz6pf21" />} title="Security Settings" subtitle="Manage your password and security options" data-id="element-4352" data-mp="3pixa2x35a4dp">
          <div data-id="element-4354" data-mp="5t95ef0zdult">
            <h4 className={`text-sm font-semibold mb-4 ${dk.textHeading}`} data-id="element-4355" data-mp="5t0omc1qm55c">
              Change Password
            </h4>
            <PasswordFields formData={formData} onChange={handleInputChange} data-id="element-4356" data-mp="fknar3fm5omv" />
          </div>

          <div className={`border-t pt-6 ${dk.border}`} data-id="element-4357" data-mp="fmhonxsgwghg">
            <div className="flex items-center justify-between" data-id="element-4358" data-mp="3e3keh0d0jrtk">
              <div data-id="element-4359" data-mp="3j0u1k9cxjre8">
                <h4 className={`text-sm font-semibold ${dk.textHeading}`} data-id="element-4360" data-mp="3j2evpxvc18bk">
                  Two-Factor Authentication
                </h4>
                <p className={`text-sm ${dk.textMuted}`} data-id="element-4361" data-mp="3o2jgw176qr84">
                  Add an extra layer of security to your account
                </p>
              </div>
              <ToggleSwitch name="twoFactorEnabled" checked={formData.twoFactorEnabled} onChange={handleInputChange} data-id="element-4362" data-mp="1396auvoiev3v" />
            </div>
          </div>

          <div className={`border-t pt-6 ${dk.border}`} data-id="element-4363" data-mp="1tdwccb7379ex">
            <h4 className={`text-sm font-semibold mb-4 ${dk.textHeading}`} data-id="element-4364" data-mp="1oes5hr7mxnrr">
              Login History
            </h4>
            <div className={`rounded-lg overflow-hidden border ${dk.border}`} data-id="element-4365" data-mp="2347scw41p926">
              {[{
            time: 'Today at 10:32 AM',
            detail: 'San Francisco, CA • Chrome on Windows',
            current: true
          }, {
            time: 'Yesterday at 8:12 PM',
            detail: 'San Francisco, CA • Chrome on Windows',
            current: false
          }, {
            time: 'June 15, 2023 at 3:45 PM',
            detail: 'San Francisco, CA • Safari on macOS',
            current: false
          }].map(({
            time,
            detail,
            current
          }, i) => <div key={time} className={`flex justify-between items-center px-4 py-3 ${i > 0 ? `border-t ${dk.border}` : ''} ${dk.surfaceMuted}`} data-id="element-4366" data-mp="mq7udqorfthk" data-mpi={i}>
                  <div data-id="element-4367" data-mp="33atfqb4nc78x">
                    <p className={`text-sm font-medium ${dk.textHeading}`} data-id="element-4368" data-mp="33dfouf76qut9">
                      {time}
                    </p>
                    <p className={`text-xs ${dk.textMuted}`} data-id="element-4369" data-mp="2tl9s6s5znp7q">{detail}</p>
                  </div>
                  {current && <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800" data-id="element-4370" data-mp="2fkqt4aji3thp">
                      Current
                    </span>}
                </div>)}
            </div>
          </div>
        </SettingsSection>}

      {/* Payment Methods */}
      {activeSection === 'payment' && <SettingsSection icon={<CreditCardIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-4372" data-mp="3xaladeuxixn" />} title="Payment Methods" subtitle="Manage your saved payment methods" headerRight={<button type="button" className={`px-4 py-2 text-sm font-medium rounded-lg ${rt.secondaryButton}`} data-id="element-4373" data-mp="1ceywg5g06g0k">
              Add Payment Method
            </button>} data-id="element-4371" data-mp="3whb12gowbar">
          <div className={`rounded-lg p-4 flex items-center justify-between border ${dk.border} ${dk.surfaceMuted}`} data-id="element-4374" data-mp="13g3ksdu6wdpv">
            <div className="flex items-center" data-id="element-4375" data-mp="18bj25freorr1">
              <CreditCardIcon className={`h-8 w-8 mr-3 ${dk.textFaint}`} data-id="element-4376" data-mp="1w4ixgtpumjy1" />
              <div data-id="element-4377" data-mp="205kztjf2bc48">
                <p className={`text-sm font-medium ${dk.textHeading}`} data-id="element-4378" data-mp="2041l3x92194k">
                  Visa ending in 4242
                </p>
                <p className={`text-xs ${dk.textMuted}`} data-id="element-4379" data-mp="1m51syfmc2c3n">Expires 12/2025</p>
              </div>
            </div>
            <div className="flex space-x-3" data-id="element-4380" data-mp="1r67qqruuopk1">
              <button type="button" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-4381" data-mp="apvat99t4zmd">
                Edit
              </button>
              <button type="button" className="text-sm font-medium text-red-600 hover:text-red-700" data-id="element-4382" data-mp="85gnqryh97m6">
                Remove
              </button>
            </div>
          </div>
          <div className={`border-t pt-6 ${dk.border}`} data-id="element-4383" data-mp="h5c2f6r8rkqy">
            <h4 className={`text-sm font-semibold mb-4 ${dk.textHeading}`} data-id="element-4384" data-mp="c7ahega99bcu">
              Billing Address
            </h4>
            <div className={`rounded-lg p-4 border ${dk.border} ${dk.surfaceMuted}`} data-id="element-4385" data-mp="3kmlrj5o7ms89">
              <p className={`text-sm ${dk.textSecondary}`} data-id="element-4386" data-mp="3gmjs735y6aw5">
                123 Main Street, Apt 4B
              </p>
              <p className={`text-sm ${dk.textSecondary}`} data-id="element-4387" data-mp="3uehuau18r6it">
                San Francisco, CA 94103
              </p>
              <p className={`text-sm ${dk.textSecondary}`} data-id="element-4388" data-mp="3pk3u8ebmwb7t">United States</p>
              <button type="button" className={`mt-3 text-sm font-medium ${rt.accentLink}`} data-id="element-4389" data-mp="31vb3cw9vctk2">
                Edit Address
              </button>
            </div>
          </div>
        </SettingsSection>}

      {/* Notifications */}
      {activeSection === 'notifications' && <SettingsSection icon={<BellIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-4391" data-mp="1ajo71o23pjn3" />} title="Notification Preferences" subtitle="Control how you receive notifications" data-id="element-4390" data-mp="15r4r4kfxycf4">
          <NotificationToggleGroup groups={[{
        key: 'emailNotifications',
        label: 'Email Notifications',
        fields: [{
          id: 'email-new-releases',
          name: 'emailNotifications.newReleases',
          checked: formData.emailNotifications.newReleases,
          label: 'New releases from followed authors'
        }, {
          id: 'email-author-updates',
          name: 'emailNotifications.authorUpdates',
          checked: formData.emailNotifications.authorUpdates,
          label: 'Author updates and announcements'
        }, {
          id: 'email-order-confirmations',
          name: 'emailNotifications.orderConfirmations',
          checked: formData.emailNotifications.orderConfirmations,
          label: 'Order confirmations and shipping updates'
        }, {
          id: 'email-promotions',
          name: 'emailNotifications.promotions',
          checked: formData.emailNotifications.promotions,
          label: 'Promotions, deals, and platform updates'
        }]
      }, {
        key: 'pushNotifications',
        label: 'Push Notifications',
        fields: [{
          id: 'push-new-releases',
          name: 'pushNotifications.newReleases',
          checked: formData.pushNotifications.newReleases,
          label: 'New releases from followed authors'
        }, {
          id: 'push-author-updates',
          name: 'pushNotifications.authorUpdates',
          checked: formData.pushNotifications.authorUpdates,
          label: 'Author updates and announcements'
        }, {
          id: 'push-order-confirmations',
          name: 'pushNotifications.orderConfirmations',
          checked: formData.pushNotifications.orderConfirmations,
          label: 'Order confirmations and shipping updates'
        }, {
          id: 'push-promotions',
          name: 'pushNotifications.promotions',
          checked: formData.pushNotifications.promotions,
          label: 'Promotions, deals, and platform updates'
        }]
      }]} onChange={handleInputChange} data-id="element-4392" data-mp="hvijyunkrik3" />
        </SettingsSection>}

      {/* Account Actions */}
      {activeSection === 'account' && <AccountActionsSection data-id="element-4393" data-mp="2ft263id1ucb1" />}
    </SettingsLayout>;
};
export default ReaderAccountSettings;